import { z } from "zod";
export const ERROR_CODES = [
    "invalid_input",
    "unauthenticated",
    "permission_denied",
    "not_found",
    "conflict",
    "approval_required",
    "budget_exceeded",
    "rate_limited",
    "exhausted",
    "not_implemented",
    "unavailable",
    "internal",
];
export const ErrorCodeSchema = z.enum(ERROR_CODES);
/** Wire shape of an error in HTTP and realtime responses. Never contains secrets. */
export const ErrorBodySchema = z.object({
    error: z.object({
        code: ErrorCodeSchema,
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
    }),
});
const HTTP_STATUS = {
    invalid_input: 400,
    unauthenticated: 401,
    permission_denied: 403,
    not_found: 404,
    conflict: 409,
    approval_required: 202,
    budget_exceeded: 402,
    rate_limited: 429,
    exhausted: 503,
    not_implemented: 501,
    unavailable: 503,
    internal: 500,
};
export function httpStatusFor(code) {
    return HTTP_STATUS[code];
}
/** Base error for every expected failure across packages. */
export class UzError extends Error {
    code;
    details;
    name = "UzError";
    constructor(code, message, details, options) {
        super(message, options);
        this.code = code;
        this.details = details;
    }
    toBody() {
        return {
            error: {
                code: this.code,
                message: this.message,
                ...(this.details ? { details: this.details } : {}),
            },
        };
    }
}
/** Optimistic concurrency failure: the entity changed since `expectedVersion`. */
export class ConflictError extends UzError {
    expectedVersion;
    actualVersion;
    name = "ConflictError";
    constructor(message, expectedVersion, actualVersion) {
        super("conflict", message, { expectedVersion, actualVersion });
        this.expectedVersion = expectedVersion;
        this.actualVersion = actualVersion;
    }
}
export class NotFoundError extends UzError {
    name = "NotFoundError";
    constructor(what, id) {
        super("not_found", `${what} not found`, { id });
    }
}
export class PermissionDeniedError extends UzError {
    missing;
    name = "PermissionDeniedError";
    constructor(missing) {
        super("permission_denied", "permission denied", { missing });
        this.missing = missing;
    }
}
export class NotImplementedError extends UzError {
    name = "NotImplementedError";
    constructor(what) {
        super("not_implemented", `${what} is not implemented yet`);
    }
}
export function isUzError(value) {
    return value instanceof UzError;
}
/** Converts any thrown value into a safe wire body. Unknown errors become `internal`. */
export function toErrorBody(err) {
    if (isUzError(err))
        return err.toBody();
    return { error: { code: "internal", message: "internal error" } };
}
//# sourceMappingURL=errors.js.map